/* eslint-disable max-lines */
import { useArtworkForm } from "@design-lab/entities/artwork";
import { usePlacementPosition } from "@design-lab/widgets/artboard/model/usePlacementPosition";
import { atom, useAtom, useSetAtom } from "jotai";
import { CloudUpload, PaintBucket, Palette, Ruler, Shirt } from "lucide-react";
import { useEffect } from "react";
import { useWatch } from "react-hook-form";

import { useCurrentComposition } from "@design-lab/entities/composition";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { trackTabNavigation } from "@design-lab/shared/lib/analytics";
import { useShowWeaveDesignLab } from "@design-lab/shared/lib/config";
import { mobileSizePlacementOpenAtom } from "@design-lab/widgets/artboard/model/state";
import type { printOnDemand } from "common/utils/models";

export type Tab = "upload" | "products" | "size" | "front" | "back";

type SurfaceMetaSides = printOnDemand.getProduct.SurfaceMeta["sides"];

type AppTabValidCallback = (
  params: ReturnType<typeof useValidation> & {
    sides?: SurfaceMetaSides;
  }
) => boolean;

type AppTabCanBeSkippedCallback = (params: {
  sides?: SurfaceMetaSides | undefined;
  isDesktop?: boolean;
}) => boolean;

export type AppTab = {
  title: string;
  mobileTitle: string;
  mobileAccordionTitle: string;
  value: Tab;
  canBeSkipped: AppTabCanBeSkippedCallback;
  icon: React.ReactNode;
  valid?: boolean;
  enabled?: boolean;
  checked?: boolean;
  isValid?: AppTabValidCallback;
  isChecked?: AppTabValidCallback;
  isEnabled?: AppTabValidCallback;
};

const tabInfoExpandedAtom = atom<boolean | undefined>(undefined);

// useVisitedTabs is provided from shared/hooks to avoid circular import

export const APP_TABS: AppTab[] = [
  {
    title: "Upload",
    mobileTitle: "Design",
    mobileAccordionTitle: "Design",
    value: "upload",
    canBeSkipped: () => false,
    icon: <CloudUpload className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isFileSelected }) => isFileSelected,
    isChecked: ({ isFileSelected }) => isFileSelected
  },
  {
    title: "Products",
    mobileTitle: "Products",
    mobileAccordionTitle: "Modify Products & Colors",
    value: "products",
    canBeSkipped: () => false,
    icon: <PaintBucket className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: () => true,
    isChecked: ({ visitedTabs }) => visitedTabs.has("products")
  },
  {
    title: "Select Sizes",
    mobileTitle: "Sizes",
    mobileAccordionTitle: "Edit Sizes",
    value: "size",
    canBeSkipped: () => false,
    icon: <Ruler className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: ({ isQuantityValid }) => isQuantityValid,
    isChecked: ({ isQuantityValid }) => isQuantityValid
  },
  {
    title: "Design Front",
    mobileTitle: "Front",
    mobileAccordionTitle: "Design Front",
    value: "front",
    canBeSkipped: params => !params.isDesktop && Boolean(params.sides?.front?.length),
    icon: <Shirt className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isAtLeastOneArtworkProcessed }) => isAtLeastOneArtworkProcessed,
    isChecked: ({ visitedTabs, isFrontArtworkUploaded }) =>
      visitedTabs.has("front") && isFrontArtworkUploaded
  },
  {
    title: "Design Back",
    mobileTitle: "Back",
    mobileAccordionTitle: "Design Back",
    value: "back",
    canBeSkipped: params => !params.isDesktop && Boolean(params.sides?.back?.length),
    icon: <Shirt className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isAtLeastOneArtworkProcessed }) => isAtLeastOneArtworkProcessed,
    isChecked: ({ visitedTabs, isBackArtworkUploaded }) =>
      visitedTabs.has("back") && isBackArtworkUploaded
  }
];

export function useTab() {
  const { lens, form: artworkForm } = useArtworkForm();
  const { isDesktop } = useResponsive();
  const currentPlacementLens = lens.focus("currentPlacement");
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const selectedFrontPlacement = usePlacementPosition(selectedFrontPlacementLens);
  const artworkFormValues = useWatch({ control: artworkForm.control });
  const validation = useValidation();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};
  const shouldShowWeaveDesignLab = useShowWeaveDesignLab();
  const setIsSizePlacementOpen = useSetAtom(mobileSizePlacementOpenAtom);

  const [isInfoExpanded, setIsInfoExpanded] = useAtom(tabInfoExpandedAtom);
  const {
    tab,
    setTab: setTabNav,
    currentIndex,
    nextTab,
    nextTabRaw,
    prevTab,
    prevTabRaw,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isProductsTab,
    isSizeTab,
    isSample
  } = useTabNavigation();

  // Apply sample mode and experiment transformations to all tabs
  const validationWithSides = { ...validation, ...(sides && { sides }) };
  const tabs: AppTab[] = APP_TABS.map(item => {
    let { title, mobileTitle, icon } = item;

    // Sample mode transformation
    if (isSample && item.value === "products") {
      title = "Colors";
      mobileTitle = "Colors";
    }

    // Create Artwork experiment transformation
    if (shouldShowWeaveDesignLab && item.value === "upload") {
      title = "Design";
      mobileTitle = "Design";
      icon = <Palette className="size-6" />;
    }

    return {
      ...item,
      title,
      mobileTitle,
      icon,
      valid: item.isValid?.(validationWithSides) ?? false,
      checked: item.isChecked?.(validationWithSides) ?? false,
      enabled: item.isEnabled?.(validationWithSides) ?? false
    };
  });

  const isValid = tabs[currentIndex]?.valid;

  const setTab = (value: Tab) => {
    setTabNav(value);

    const selectedFrontArtworkId = artworkFormValues?.[selectedFrontPlacement.placement]?.artworkId;
    const backArtworkId = artworkFormValues?.back?.artworkId;

    const onlyHasFrontArtwork = selectedFrontArtworkId && !backArtworkId;
    const onlyHasBackArtwork = backArtworkId && !selectedFrontArtworkId;

    const isNonDesignTab = value === "upload" || value === "products" || value === "size";

    if (value === "front" || (isNonDesignTab && onlyHasFrontArtwork)) {
      currentPlacement.setPlacement(selectedFrontPlacement.placement);
    } else if (value === "back" || (isNonDesignTab && onlyHasBackArtwork)) {
      currentPlacement.setPlacement("back");
    }
  };

  useEffect(() => {
    if (tab === "back" && currentPlacement.placement !== "back") {
      currentPlacement.setPlacement("back");
    } else if (tab === "front" && currentPlacement.placement === "back") {
      currentPlacement.setPlacement(selectedFrontPlacement.placement);
    }
  }, [
    tab,
    currentPlacement.placement,
    selectedFrontPlacement.placement,
    currentPlacement,
    selectedFrontPlacement
  ]);

  const next = () => {
    setIsSizePlacementOpen(false);
    if (nextTabRaw) {
      setTab(nextTabRaw.value);
      trackTabNavigation(nextTabRaw.value, "next");
    }
  };

  const back = () => {
    setIsSizePlacementOpen(false);
    if (prevTabRaw) {
      setTab(prevTabRaw.value);
      trackTabNavigation(prevTabRaw.value, "tabs");
    }
  };

  const toggleInfoExpanded = () => {
    setIsInfoExpanded(!isInfoExpanded);
  };

  useEffect(() => {
    if (isInfoExpanded === undefined) {
      setIsInfoExpanded(isDesktop);
    }
  }, [isDesktop, isInfoExpanded, setIsInfoExpanded]);

  return {
    tabs,
    tab,
    setTab,
    nextTab,
    prevTab,
    next,
    back,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isValid,
    isProductsTab,
    isSizeTab,
    isInfoExpanded,
    toggleInfoExpanded
  };
}
